import { heroLook } from '@shared/looks';
import { FORTUNE_RULES } from '../data/fortune';
import { SAVE_VERSION, defaultCounters, defaultSettings, newSave, type SaveData } from './model';
import { MAX_ITEM_LEVEL } from './upgrade';
import { FREE_HERO_SLOTS, MAX_HERO_SLOTS } from './heroSlots';

export const SAVE_KEY = 'paper-legends/save';
export const BACKUP_KEY = 'paper-legends/save.bak';

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const arr = <T>(v: unknown, guard: (x: unknown) => x is T): T[] => (Array.isArray(v) ? v.filter(guard) : []);
const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/**
 * Bring any parsed JSON into a valid SaveData: fills missing fields, drops
 * malformed entries and migrates older versions. Never throws.
 */
export function sanitizeSave(raw: unknown): SaveData {
  const base = newSave();
  if (!isObj(raw)) return base;
  const isHero = (h: unknown): h is SaveData['heroes'][number] =>
    isObj(h) && typeof h.id === 'string' && typeof h.name === 'string' && typeof h.classId === 'string' && typeof h.level === 'number';
  const isMonster = (m: unknown): m is SaveData['monsters'][number] =>
    isObj(m) && typeof m.id === 'string' && typeof m.name === 'string' && typeof m.hp === 'number' && typeof m.atk === 'number';
  const isMemory = (m: unknown): m is SaveData['memories'][number] => isObj(m) && typeof m.id === 'string' && typeof m.title === 'string';
  const isFriend = (f: unknown): f is SaveData['friends'][number] => isObj(f) && typeof f.id === 'string' && typeof f.name === 'string';
  const isStr = (s: unknown): s is string => typeof s === 'string';

  const heroes = arr(raw.heroes, isHero).map((h) => ({
    ...h,
    alloc: { hp: 0, atk: 0, def: 0, luck: 0, ...(isObj(h.alloc) ? h.alloc : {}) },
    equipment: isObj(h.equipment) ? h.equipment : {},
    drawing: isObj(h.drawing) ? h.drawing : null,
    look: isStr(h.look) && heroLook(h.look) ? h.look : null,
    exp: num(h.exp, 0),
    statPoints: num(h.statPoints, 0),
    lossStreak: Math.max(0, Math.floor(num(h.lossStreak, 0))),
    injuredUntil: Math.max(0, num(h.injuredUntil, 0)),
  }));
  const fight = isObj(raw.battleInProgress) ? raw.battleInProgress : null;
  const battleInProgress: SaveData['battleInProgress'] =
    fight && isStr(fight.heroId) && isStr(fight.monsterId) ? { heroId: fight.heroId, monsterId: fight.monsterId, monsterName: isStr(fight.monsterName) ? fight.monsterName : '', at: num(fight.at, 0) } : null;
  const counters = { ...defaultCounters(), ...(isObj(raw.counters) ? raw.counters : {}) } as SaveData['counters'];
  const settings = { ...defaultSettings(), ...(isObj(raw.settings) ? raw.settings : {}) } as SaveData['settings'];
  if (!['auto', 'en', 'th'].includes(settings.language)) settings.language = 'auto';
  if (settings.drawMode !== 'easy' && settings.drawMode !== 'pro') settings.drawMode = 'easy';
  if (settings.renderMode !== 'cpu' && settings.renderMode !== 'gpu') settings.renderMode = 'cpu';
  if (typeof settings.traceGame !== 'boolean') settings.traceGame = true;
  const online = isObj(raw.online) && isStr(raw.online.token) && isStr(raw.online.playerId) ? (raw.online as unknown as SaveData['online']) : null;
  const activeHeroId = isStr(raw.activeHeroId) && heroes.some((h) => h.id === raw.activeHeroId) ? raw.activeHeroId : (heroes[0]?.id ?? null);

  const ageGroup = raw.ageGroup === 'under13' || raw.ageGroup === 'teen' || raw.ageGroup === 'adult' ? raw.ageGroup : null;
  const cosmeticsRaw = isObj(raw.cosmetics) ? raw.cosmetics : {};
  const owned = arr(cosmeticsRaw.owned, isStr);
  const stamp = isStr(cosmeticsRaw.stamp) && owned.includes(cosmeticsRaw.stamp) ? cosmeticsRaw.stamp : null;

  const boardRaw = isObj(raw.board) ? raw.board : {};
  const board: SaveData['board'] = {
    pos: Number.isInteger(boardRaw.pos) && (boardRaw.pos as number) >= 0 && (boardRaw.pos as number) < 20 ? (boardRaw.pos as number) : 0,
    rolls: Math.max(0, Math.min(10, Math.floor(num(boardRaw.rolls, 5)))),
    day: isStr(boardRaw.day) ? boardRaw.day : '',
  };
  const contestRaw = isObj(raw.contest) ? raw.contest : {};
  const communityRaw = isObj(raw.community) ? raw.community : {};
  const dailyRaw = isObj(raw.daily) ? raw.daily : {};
  const daily: SaveData['daily'] = {
    day: isStr(dailyRaw.day) ? dailyRaw.day : '',
    base: { ...defaultCounters(), ...(isObj(dailyRaw.base) ? dailyRaw.base : {}) } as SaveData['counters'],
    quests: arr(dailyRaw.quests, isStr),
    claimed: arr(dailyRaw.claimed, isStr),
    streak: Math.max(0, num(dailyRaw.streak, 0)),
    lastFull: isStr(dailyRaw.lastFull) ? dailyRaw.lastFull : null,
  };
  const fortuneRaw = isObj(raw.fortune) ? raw.fortune : {};
  const count = (v: unknown): number => Math.max(0, Math.floor(num(v, 0)));
  const fortune: SaveData['fortune'] = {
    tickets: Math.min(FORTUNE_RULES.ticketCap, count(fortuneRaw.tickets)),
    lastFreeDay: isStr(fortuneRaw.lastFreeDay) ? fortuneRaw.lastFreeDay : null,
    draws: count(fortuneRaw.draws),
    sinceRare: count(fortuneRaw.sinceRare),
    sinceEpic: count(fortuneRaw.sinceEpic),
    sinceLegendary: count(fortuneRaw.sinceLegendary),
    coinDay: isStr(fortuneRaw.coinDay) ? fortuneRaw.coinDay : '',
    coinDraws: count(fortuneRaw.coinDraws),
  };
  return {
    version: SAVE_VERSION,
    ageGroup,
    cosmetics: { owned, stamp },
    contest: { votedRounds: arr(contestRaw.votedRounds, isStr), prizes: arr(contestRaw.prizes, isStr) },
    community: {
      bossPrizes: arr(communityRaw.bossPrizes, isStr),
      classId: isStr(communityRaw.classId) ? communityRaw.classId : null,
      classPrizes: arr(communityRaw.classPrizes, isStr),
      cardSent: isStr(communityRaw.cardSent) ? communityRaw.cardSent : '',
    },
    daily,
    fortune,
    heroSlots: Math.min(MAX_HERO_SLOTS - FREE_HERO_SLOTS, count(raw.heroSlots)),
    heroes,
    activeHeroId,
    battleInProgress,
    monsters: arr(raw.monsters, isMonster).map((m) => ({ ...m, drawing: isObj(m.drawing) ? m.drawing : null, wins: num(m.wins, 0), losses: num(m.losses, 0) })),
    coins: Math.max(0, num(raw.coins, base.coins)),
    items: arr(raw.items, isStr),
    itemLevels: isObj(raw.itemLevels)
      ? Object.fromEntries(Object.entries(raw.itemLevels).filter((e): e is [string, number] => Number.isInteger(e[1]) && (e[1] as number) >= 1 && (e[1] as number) <= MAX_ITEM_LEVEL))
      : {},
    story: (() => {
      const st = isObj(raw.story) ? raw.story : {};
      const time = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : null);
      const startedAt = time(st.startedAt);
      const doneAt = time(st.doneAt);
      return { startedAt, doneAt: startedAt !== null && doneAt !== null && doneAt > startedAt ? doneAt : null };
    })(),
    defeatedPresets: arr(raw.defeatedPresets, isStr),
    monsterStars: isObj(raw.monsterStars)
      ? Object.fromEntries(Object.entries(raw.monsterStars).filter((e): e is [string, number] => Number.isInteger(e[1]) && (e[1] as number) >= 1 && (e[1] as number) <= 3))
      : {},
    chaptersDone: arr(raw.chaptersDone, isStr),
    quests: { claimed: isObj(raw.quests) ? arr(raw.quests.claimed, isStr) : [] },
    counters,
    board,
    memories: arr(raw.memories, isMemory),
    friends: arr(raw.friends, isFriend),
    settings,
    online,
    savedAt: num(raw.savedAt, 0),
  };
}

export type LoadResult = { save: SaveData; source: 'main' | 'backup' | 'new'; error?: string };

export function loadSave(storage: KeyValueStorage): LoadResult {
  for (const [key, source] of [[SAVE_KEY, 'main'], [BACKUP_KEY, 'backup']] as const) {
    try {
      const text = storage.getItem(key);
      if (!text) continue;
      return { save: sanitizeSave(JSON.parse(text)), source };
    } catch (err) {
      console.warn(`save slot ${key} unreadable`, err);
    }
  }
  return { save: newSave(), source: 'new' };
}

/** Writes the save; keeps the previous good copy as a backup. Throws on quota errors. */
export function writeSave(storage: KeyValueStorage, save: SaveData): void {
  save.savedAt = Date.now();
  const text = JSON.stringify(save);
  const prev = storage.getItem(SAVE_KEY);
  if (prev) storage.setItem(BACKUP_KEY, prev);
  storage.setItem(SAVE_KEY, text);
}
